import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {useAuth} from "../auth";
import {ApiError} from "../api/client";
import {chatAccess, chatMessages, chatSeen, downloadChatFile, retryChatMessage, sendChatMessage} from "../api/orderChat";
import type {ChatMessage} from "../api/orderChat";
import {adminChatMessages, downloadAdminChatFile, sendAdminChatMessage, retryAdminChatMessage} from "../api/admin/orderChats";
import "./order-chat.css";

export const CHAT_REFRESH_MS = 4000;
const FILE_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

export function OrderChatLink({orderID, onOpen}: {orderID: string; onOpen?: (orderID: string) => void}): JSX.Element | null {
    const {user, token} = useAuth();
    const trader = Boolean(user?.roles?.includes("trader") && !user.roles.includes("admin"));
    const [available, setAvailable] = useState<{orderID: string; token: string} | null>(null);
    useEffect(() => {
        let active = true;
        setAvailable(null);
        if (trader && token) {
            void chatAccess(token, orderID).then(result => {
                if (active && result.enabled) setAvailable({orderID, token});
            }).catch(() => undefined);
        }
        return () => { active = false; };
    }, [orderID, token, trader]);
    if (!trader || !token || available?.orderID !== orderID || available.token !== token) return null;
    return <button className="btn btn-ghost" type="button" onClick={() => onOpen?.(orderID)}>Чат по заявке</button>;
}

export function mergeChatMessages(previous: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
    const byID = new Map(previous.map(message => [message.id, message]));
    incoming.forEach(message => byID.set(message.id, message));
    return Array.from(byID.values()).sort((a, b) => a.seq - b.seq);
}

interface OrderChatProps {orderID: string; token: string; mode?: "trader" | "admin";}

export function OrderChat(props: OrderChatProps): JSX.Element {
    return <OrderChatBody key={`${props.token}:${props.orderID}:${props.mode || "trader"}`} {...props}/>;
}

function OrderChatBody({orderID, token, mode = "trader"}: OrderChatProps): JSX.Element {
    const isAdmin = mode === "admin";
    const readMessages = isAdmin ? adminChatMessages : chatMessages;
    const sendMessage = isAdmin ? sendAdminChatMessage : sendChatMessage;
    const retryMessage = isAdmin ? retryAdminChatMessage : retryChatMessage;
    const downloadFile = isAdmin ? downloadAdminChatFile : downloadChatFile;
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [more, setMore] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [allowed, setAllowed] = useState(true);
    const [ownerAvailable, setOwnerAvailable] = useState(true);
    const [error, setError] = useState("");
    const [text, setText] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const input = useRef<HTMLInputElement>(null);
    const pending = useRef<{key: string; text: string; file: File | null} | null>(null);
    const mounted = useRef(true);
    const busyRead = useRef(false);
    const seen = useRef(0);
    const messageList = useRef<HTMLOListElement>(null);
    const followLatest = useRef(true);
    const previousList = useRef({firstSeq: 0, height: 0});
    const viewed = useRef(new Set<number>());
    const markingSeen = useRef(false);
    const currentMessages = useRef<ChatMessage[]>([]);
    const statusRefreshIndex = useRef(0);

    const markVisible = useCallback((): void => {
        const list = messageList.current;
        if (isAdmin || !list || document.hidden || !navigator.onLine) return;
        const bounds = list.getBoundingClientRect();
        if (bounds.height <= 0) return;
        list.querySelectorAll<HTMLElement>("li[data-seq]").forEach(element => {
            const rect = element.getBoundingClientRect();
            if (rect.height > 0 && rect.bottom > bounds.top && rect.top < bounds.bottom) viewed.current.add(Number(element.dataset.seq));
        });
        let cursor = seen.current;
        while (viewed.current.has(cursor + 1)) cursor++;
        if (cursor === seen.current || markingSeen.current) return;
        markingSeen.current = true;
        void chatSeen(token, orderID, cursor).then(() => {
            if (!mounted.current) return;
            seen.current = Math.max(seen.current, cursor);
            viewed.current.forEach(seq => { if (seq <= cursor) viewed.current.delete(seq); });
        }).catch(() => undefined).finally(() => { markingSeen.current = false; });
    }, [orderID, token, isAdmin]);

    useLayoutEffect(() => {
        currentMessages.current = messages;
        const list = messageList.current;
        if (!list) return;
        const firstSeq = messages[0]?.seq ?? 0;
        const previous = previousList.current;
        if (previous.firstSeq && firstSeq && firstSeq < previous.firstSeq) {
            list.scrollTop += list.scrollHeight - previous.height;
        } else if (followLatest.current) {
            list.scrollTop = list.scrollHeight;
        }
        previousList.current = {firstSeq, height: list.scrollHeight};
        markVisible();
    }, [messages, markVisible]);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const reportError = useCallback((failure: unknown): void => {
        if (!mounted.current) return;
        if (failure instanceof ApiError && [401, 403, 404].includes(failure.status)) {
            setMessages([]);
            setAllowed(false);
            setError(isAdmin ? "Чат недоступен. Проверьте права доступа и настройки мерчанта." : "Чат недоступен. Возможно, у заявки сменился ответственный.");
        } else {
            setError("Не удалось выполнить запрос. Проверьте соединение и повторите.");
        }
    }, [isAdmin]);

    useEffect(() => {
        let disposed = false;
        let firstPage = true;
        let previousCursor = 0;
        const refresh = async (): Promise<void> => {
            if (disposed || busyRead.current || document.visibilityState === "hidden" || !navigator.onLine) return;
            busyRead.current = true;
            try {
                const page = await readMessages(token, orderID);
                if (disposed) return;
                const incoming = page.messages || [];
                const firstCursor = Math.min(...incoming.map(message => message.seq));
                seen.current = Math.max(seen.current, page.seen_cursor || 0);
                const pendingOld = currentMessages.current.filter(message => message.sender !== "client" && message.delivery_status === "pending" && message.seq < firstCursor);
                let statusUpdates: ChatMessage[] = [];
                if (pendingOld.length) {
                    const target = pendingOld[statusRefreshIndex.current++ % pendingOld.length];
                    const statuses = await readMessages(token, orderID, target.seq + 1);
                    if (disposed) return;
                    const loadedIDs = new Set(currentMessages.current.map(message => message.id));
                    statusUpdates = (statuses.messages || []).filter(message => loadedIDs.has(message.id));
                }
                const gap = previousCursor > 0 && firstCursor > previousCursor + 1;
                setMessages(current => mergeChatMessages(gap ? [] : current, [...statusUpdates, ...incoming]));
                if (firstPage || gap) setMore(page.has_more);
                firstPage = false;
                setLoaded(true);
                setAllowed(isAdmin || page.owner_available);
                setOwnerAvailable(page.owner_available);
                setError("");
                const cursor = Math.max(0, ...(page.messages || []).map(message => message.seq));
                previousCursor = Math.max(previousCursor, cursor);
            } catch (failure) {
                if (!disposed) reportError(failure);
            } finally {
                busyRead.current = false;
            }
        };
        void refresh();
        const timer = window.setInterval(() => void refresh(), CHAT_REFRESH_MS);
        const onWake = (): void => { void refresh(); };
        window.addEventListener("focus", onWake);
        window.addEventListener("online", onWake);
        document.addEventListener("visibilitychange", onWake);
        return () => {
            disposed = true;
            window.clearInterval(timer);
            window.removeEventListener("focus", onWake);
            window.removeEventListener("online", onWake);
            document.removeEventListener("visibilitychange", onWake);
        };
    }, [orderID, token, reportError, readMessages, isAdmin]);

    const loadEarlier = async (): Promise<void> => {
        if (busyRead.current || messages.length === 0) return;
        busyRead.current = true;
        try {
            const page = await readMessages(token, orderID, messages[0].seq);
            if (!mounted.current) return;
            setMessages(current => mergeChatMessages(current, page.messages || []));
            setMore(page.has_more);
        } catch (failure) { reportError(failure); }
        finally { busyRead.current = false; }
    };

    const send = async (event: React.FormEvent): Promise<void> => {
        event.preventDefault();
        if (!loaded || busy || !allowed || (!text.trim() && !file)) return;
        if (text.length > (file ? 900 : 3500)) {
            setError(file ? "Подпись к файлу — до 900 символов." : "Сообщение — до 3500 символов.");
            return;
        }
        if (!pending.current || pending.current.text !== text || pending.current.file !== file) {
            pending.current = {key: crypto.randomUUID(), text, file};
        }
        const draft = pending.current;
        setBusy(true);
        setError("");
        try {
            const message = await sendMessage(token, orderID, draft.key, draft.text, draft.file);
            if (!mounted.current) return;
            setMessages(current => mergeChatMessages(current, [message]));
            setText(""); setFile(null); pending.current = null;
            if (input.current) input.current.value = "";
        } catch (failure) { reportError(failure); }
        finally { if (mounted.current) setBusy(false); }
    };

    const fileChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const next = event.target.files?.[0] || null;
        if (next && (next.size > 15 * 1024 * 1024 || !FILE_TYPES.includes(next.type))) {
            setError("Подойдёт JPG, PNG, WEBP или PDF размером до 15 МБ.");
            event.target.value = "";
            setFile(null);
            return;
        }
        setFile(next); setError("");
    };

    const retry = async (message: ChatMessage): Promise<void> => {
        if (busy || !allowed || message.sender !== mode) return;
        if (message.error?.startsWith("delivery_uncertain") && !window.confirm("Telegram мог уже получить сообщение. Повторная отправка может создать дубль. Отправить ещё раз?")) return;
        setBusy(true);
        try {
            await retryMessage(token, orderID, message.id);
            if (mounted.current) setMessages(current => current.map(item => item.id === message.id ? {...item, delivery_status: "pending", error: undefined} : item));
        } catch (failure) { reportError(failure); }
        finally { if (mounted.current) setBusy(false); }
    };

    return <section className="order-chat" aria-label="Переписка по заявке">
        <p className="order-chat-hint">Текст и файлы доступны и после завершения заявки. Файл в чате не подтверждает оплату.</p>
        {isAdmin && <p className="order-chat-hint">Ваши сообщения придут клиенту с подписью «Администратор».</p>}
        {isAdmin && loaded && allowed && !ownerAvailable && <p className="order-chat-owner-warning">Нет доступного ответственного. Сообщения клиента сохраняются в чате.</p>}
        {error && <p role="alert" className="danger-text">{error}</p>}
        {!loaded && !error && <p role="status">Загружаем переписку…</p>}
        {more && allowed && <button className="btn btn-ghost" onClick={() => void loadEarlier()}>Более ранние сообщения</button>}
        <ol ref={messageList} className="order-chat-messages" aria-label="Сообщения" onScroll={() => {
            const list = messageList.current;
            if (list) followLatest.current = list.scrollHeight - list.scrollTop - list.clientHeight < 48;
            markVisible();
        }}>
            {messages.map(message => <li key={message.id} data-seq={message.seq} className={`order-chat-message ${message.sender}`}>
                <div className="order-chat-meta"><strong>{message.sender === "client" ? "Клиент" : message.sender === "admin" ? "Администратор" : "Трейдер"}</strong><time dateTime={message.created_at}>{new Date(message.created_at).toLocaleString("ru-RU")}</time></div>
                {message.text && <p>{message.text}</p>}
                {message.file && <button className="btn btn-ghost" onClick={() => void downloadFile(token, orderID, message).catch(reportError)}>{message.file.name} · {Math.ceil(message.file.size / 1024)} КБ</button>}
                {message.sender !== "client" && <span className="order-chat-status">{message.delivery_status === "delivered" ? "Доставлено Telegram" : message.delivery_status === "failed" ? (message.error?.startsWith("delivery_uncertain") ? "Доставка не подтверждена · при повторе возможен дубль" : "Не доставлено") : "Принято · ожидает доставки"}</span>}
                {message.sender === mode && message.delivery_status === "failed" && allowed && <button className="btn btn-ghost" disabled={busy} onClick={() => void retry(message)}>Повторить доставку</button>}
            </li>)}
        </ol>
        {loaded && messages.length === 0 && allowed && <p>Сообщений пока нет.</p>}
        {<form onSubmit={event => void send(event)} className="order-chat-compose">
            <label>Сообщение<textarea aria-label="Сообщение" value={text} maxLength={file ? 900 : 3500} disabled={busy || !allowed} onChange={event => setText(event.target.value)} rows={3}/></label>
            <label>Вложение<input ref={input} type="file" accept={FILE_TYPES.join(",")} disabled={busy || !allowed} onChange={fileChanged}/></label>
            {file && <span>{file.name}</span>}
            <button className="btn btn-primary" type="submit" disabled={!loaded || !allowed || busy || (!text.trim() && !file)}>{busy ? "Отправляем…" : "Отправить"}</button>
        </form>}
    </section>;
}
